import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';

/**
 * Re-validates the fields currently showing an error when the language changes.
 * Keyed on `i18n.language`, not the `languageChanged` event — that fires before
 * React re-renders, so validation would use the outgoing schema.
 * Errored fields only, never `root`: trigger() unsets any name the schema returns
 * no issue for, which would surface untouched fields and delete server errors.
 */
export const useRetranslateErrors = <T extends FieldValues>(form: UseFormReturn<T>) => {
  const { i18n } = useTranslation();

  useEffect(() => {
    const fields = Object.keys(form.formState.errors)
      .filter((name) => name !== 'root' && !name.startsWith('root.')) as Path<T>[];
    if (fields.length > 0) void form.trigger(fields);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- errors are sampled, not tracked; listing them re-runs this on every validation
  }, [i18n.language, form]);
};
